import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  CardMedia,
  useTheme,
  useMediaQuery,
  Button,
  Stack,
  Chip,
  Divider,
  Paper,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import SecurityIcon from "@mui/icons-material/Security";
import BoltIcon from "@mui/icons-material/Bolt";
import VerifiedIcon from "@mui/icons-material/Verified";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaidIcon from "@mui/icons-material/Paid";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import PlaceIcon from "@mui/icons-material/Place";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

function Home() {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const navLinks = [
    { label: "Accueil", href: "#accueil" },
    { label: "À propos", href: "#apropos" },
    { label: "Services", href: "#services" },
    { label: "Contact", href: "#contact" },
    { label: "Retour à l'accueil", href: "welcome.html" },
  ];

  const services = [
    {
      icon: <PaidIcon />,
      title: "Transfert de fonds",
      desc: "Envoi et réception entre la RDC et la Chine, avec suivi et assistance.",
    },
    {
      icon: <LocalShippingIcon />,
      title: "Fret & logistique",
      desc: "Gestion et acheminement de colis avec accompagnement et conseils.",
    },
    {
      icon: <SupportAgentIcon />,
      title: "Assistance client",
      desc: "Support réactif et accompagnement personnalisé à chaque étape.",
    },
  ];

  const highlights = [
    { icon: <SecurityIcon />, label: "Sécurité" },
    { icon: <BoltIcon />, label: "Rapidité" },
    { icon: <VerifiedIcon />, label: "Confiance" },
  ];

  return (
    <Box sx={{ bgcolor: "background.default" }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        color="transparent"
        sx={{
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: "rgba(255,255,255,0.75)",
        }}
      >
        <Toolbar sx={{ minHeight: 72 }}>
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Stack direction="row" alignItems="center" spacing={1} sx={{ flexGrow: 1 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "primary.main",
                color: "primary.contrastText",
                fontWeight: 900,
              }}
            >
              D
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={900} lineHeight={1.1}>
                DIEU EST NOTRE FORCE
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Transfert de fonds • Fret RDC ↔ Chine
              </Typography>
            </Box>
          </Stack>

          {!isMobile && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {navLinks.slice(0, 4).map((link) => (
                <Button
                  key={link.href}
                  color="inherit"
                  href={link.href}
                  sx={{ fontWeight: 700 }}
                >
                  {link.label}
                </Button>
              ))}
              <Button
                variant="contained"
                href="#contact"
                sx={{ ml: 1, fontWeight: 800, borderRadius: 2 }}
              >
                Nous contacter
              </Button>
            </Stack>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer for mobile */}
      <Drawer anchor="left" open={drawerOpen} onClose={() => setDrawerOpen(false)}>
        <Box sx={{ width: 280, p: 2 }}>
          <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
            Menu
          </Typography>
          <Divider sx={{ mb: 1 }} />
          <List sx={{ p: 0 }}>
            {navLinks.map((link) => (
              <ListItemButton
                key={link.href}
                component="a"
                href={link.href}
                onClick={() => setDrawerOpen(false)}
                sx={{ borderRadius: 2 }}
              >
                <ListItemText primary={link.label} />
              </ListItemButton>
            ))}
          </List>
          <Button
            fullWidth
            variant="contained"
            href="#contact"
            onClick={() => setDrawerOpen(false)}
            sx={{ mt: 2, borderRadius: 2, fontWeight: 800 }}
          >
            Nous contacter
          </Button>
        </Box>
      </Drawer>

      {/* spacer */}
      <Box sx={{ height: 72 }} />

      {/* Hero Section */}
      <Box
        sx={{
          position: "relative",
          minHeight: { xs: 520, md: 640 },
          display: "flex",
          alignItems: "center",
          color: "#fff",
          overflow: "hidden",
          borderBottomLeftRadius: { xs: 0, md: 24 },
          borderBottomRightRadius: { xs: 0, md: 24 },
        }}
        id="top"
      >
        {/* Background image */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: "url(/accueil1.jpeg)",
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
            transform: "scale(1.02)",
          }}
        />
        {/* Overlay */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(90deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.55) 45%, rgba(0,0,0,0.25) 100%)",
          }}
        />

        <Container sx={{ position: "relative", py: { xs: 6, md: 10 } }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {highlights.map((h) => (
                    <Chip
                      key={h.label}
                      icon={h.icon}
                      label={h.label}
                      sx={{
                        bgcolor: "rgba(255,255,255,0.12)",
                        color: "#fff",
                        border: "1px solid rgba(255,255,255,0.2)",
                        "& .MuiChip-icon": { color: "#fff" },
                        fontWeight: 700,
                      }}
                    />
                  ))}
                </Stack>

                <Typography
                  variant={isMobile ? "h3" : "h2"}
                  fontWeight={900}
                  sx={{ letterSpacing: 0.2 }}
                >
                  Transfert de fonds & fret
                  <br />
                  <Box component="span" sx={{ opacity: 0.95 }}>
                    RDC ↔ Chine
                  </Box>
                </Typography>

                <Typography
                  variant="h6"
                  sx={{ opacity: 0.92, maxWidth: 620, lineHeight: 1.6 }}
                >
                  Partenaire de confiance pour faciliter vos échanges : envoi d’argent,
                  réception, logistique et accompagnement client — avec une équipe disponible
                  à Kinshasa et en Chine.
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <Button
                    variant="contained"
                    href="#services"
                    size="large"
                    sx={{ borderRadius: 2, fontWeight: 900, px: 3 }}
                  >
                    Découvrir nos services
                  </Button>
                  <Button
                    variant="outlined"
                    href="#contact"
                    size="large"
                    sx={{
                      borderRadius: 2,
                      fontWeight: 900,
                      px: 3,
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.5)",
                      "&:hover": { borderColor: "#fff" },
                    }}
                  >
                    Contact & horaires
                  </Button>
                </Stack>

                <Stack direction="row" spacing={1} sx={{ opacity: 0.9 }}>
                  <PlaceIcon fontSize="small" />
                  <Typography variant="body2">Kinshasa • Guangzhou (Chine)</Typography>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  bgcolor: "rgba(255,255,255,0.10)",
                  border: "1px solid rgba(255,255,255,0.18)",
                  color: "#fff",
                  borderRadius: 3,
                  p: 2.5,
                }}
              >
                <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 1 }}>
                  Informations rapides
                </Typography>
                <Divider sx={{ borderColor: "rgba(255,255,255,0.18)", mb: 2 }} />

                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AccessTimeIcon fontSize="small" />
                    <Typography variant="body2">
                      Lun–Ven: 8h00–18h00 • Sam: 9h00–15h00 • Dim: Fermé
                    </Typography>
                  </Stack>

                  <Stack spacing={0.5}>
                    <Typography variant="body2" fontWeight={800}>
                      Contacts
                    </Typography>
                    <Typography variant="body2">+86 153 6003 6746 (Michel)</Typography>
                    <Typography variant="body2">+243 89 938 0387 (Regine)</Typography>
                    <Typography variant="body2">contact@dieunotreforce.com</Typography>
                  </Stack>

                  <Button
                    variant="contained"
                    href="#contact"
                    sx={{ borderRadius: 2, fontWeight: 900 }}
                  >
                    Obtenir de l’aide
                  </Button>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Accueil - IMAGE LEFT, TEXT RIGHT */}
      <Container id="accueil" sx={{ py: { xs: 6, md: 9 } }}>
        <Grid container spacing={4} alignItems="center">
          {/* IMAGE */}
          <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
            <Card sx={{ borderRadius: 4, overflow: "hidden" }}>
              <CardMedia
                component="img"
                image="/accueil.jpeg"
                alt="Accueil"
                sx={{
                  height: { xs: 240, md: 320 },
                  objectFit: "cover",
                }}
              />
            </Card>
          </Grid>

          {/* TEXT */}
          <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
            <Stack spacing={2}>
              <Typography variant="h4" fontWeight={900}>
                Bienvenue
              </Typography>
              <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                Nous facilitons les transferts d’argent internationaux et le fret entre la
                RDC et la Chine. Notre priorité : une expérience simple, claire et fiable,
                avec un suivi et une assistance à chaque étape.
              </Typography>

              <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                <Card sx={{ borderRadius: 3, flex: 1 }}>
                  <CardContent>
                    <Typography fontWeight={900}>Fiabilité</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Process clair, traitement rigoureux et accompagnement.
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ borderRadius: 3, flex: 1 }}>
                  <CardContent>
                    <Typography fontWeight={900}>Rapidité</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Prise en charge efficace et support réactif.
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>

              <Button
                variant="outlined"
                href="#apropos"
                sx={{ width: "fit-content", borderRadius: 2, fontWeight: 900 }}
              >
                En savoir plus
              </Button>
            </Stack>
          </Grid>
        </Grid>
      </Container>

      {/* À propos - IMAGE LEFT, TEXT RIGHT */}
      <Box id="apropos" sx={{ bgcolor: "grey.50", py: { xs: 6, md: 9 } }}>
        <Container>
          <Grid container spacing={4} alignItems="center">
            {/* IMAGE */}
            <Grid item xs={12} md={6} order={{ xs: 2, md: 1 }}>
              <Card sx={{ borderRadius: 4, overflow: "hidden" }}>
                <CardMedia
                  component="img"
                  image="/apropos.jpeg"
                  alt="À propos"
                  sx={{
                    height: { xs: 240, md: 320 },
                    objectFit: "cover",
                  }}
                />
              </Card>
            </Grid>

            {/* TEXT */}
            <Grid item xs={12} md={6} order={{ xs: 1, md: 2 }}>
              <Stack spacing={2}>
                <Typography variant="h4" fontWeight={900}>
                  À propos
                </Typography>

                <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                  Fondée en 2020, notre agence accompagne les transferts de fonds et la
                  logistique entre la RDC et la Chine, avec un focus sur la sécurité, la
                  transparence et la satisfaction client.
                </Typography>

                <Card variant="outlined" sx={{ borderRadius: 3 }}>
                  <CardContent>
                    <Typography fontWeight={900} sx={{ mb: 1 }}>
                      Nos bureaux à Kinshasa
                    </Typography>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PlaceIcon fontSize="small" />
                        <Typography variant="body2">
                          Galerie Grand Itaga, 107 Av. Itaga, C/Kinshasa
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <PlaceIcon fontSize="small" />
                        <Typography variant="body2">
                          Immeuble KAVALI-CENTER, Av. Mutombo Katshi, Gombe
                        </Typography>
                      </Stack>
                    </Stack>
                  </CardContent>
                </Card>

                <Button
                  variant="contained"
                  href="#services"
                  sx={{ width: "fit-content", borderRadius: 2, fontWeight: 900 }}
                >
                  Voir nos services
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services */}
      <Container id="services" sx={{ py: { xs: 6, md: 9 } }}>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Typography variant="h4" fontWeight={900}>
            Services
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 760, lineHeight: 1.8 }}>
            Des solutions simples et efficaces pour transférer des fonds et gérer vos
            envois entre la RDC et la Chine.
          </Typography>
        </Stack>

        <Grid container spacing={3}>
          {services.map((s) => (
            <Grid key={s.title} item xs={12} md={4}>
              <Card sx={{ height: "100%", borderRadius: 4 }}>
                <CardContent>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: "grid",
                      placeItems: "center",
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                      mb: 1.5,
                    }}
                  >
                    {s.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={900} gutterBottom>
                    {s.title}
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.8 }}>
                    {s.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        <Card
          sx={{
            mt: 3,
            borderRadius: 4,
            bgcolor: "grey.50",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={8}>
                <Typography fontWeight={900} sx={{ mb: 0.5 }}>
                  Besoin d’une information rapide ?
                </Typography>
                <Typography color="text.secondary">
                  Notre équipe vous répond pour vous orienter vers la meilleure solution.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4} sx={{ textAlign: { xs: "left", md: "right" } }}>
                <Button
                  variant="contained"
                  href="#contact"
                  size="large"
                  sx={{ borderRadius: 2, fontWeight: 900 }}
                >
                  Parler à un agent
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>

      {/* Contact */}
      <Box id="contact" sx={{ bgcolor: "#0F172A", color: "white", py: { xs: 6, md: 9 } }}>
        <Container>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontWeight={900} gutterBottom>
                Kinshasa (RDC)
              </Typography>

              <Stack spacing={1.2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PlaceIcon fontSize="small" />
                  <Typography>Galerie Grand Itaga, C/Kinshasa</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PlaceIcon fontSize="small" />
                  <Typography>Immeuble KAVALI-CENTER, Gombe</Typography>
                </Stack>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", my: 1 }} />

                <Typography>+86 153 6003 6746 (Michel)</Typography>
                <Typography>+243 89 938 0387 (Regine)</Typography>
                <Typography>contact@dieunotreforce.com</Typography>
              </Stack>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h5" fontWeight={900} gutterBottom>
                Chine (Guangzhou)
              </Typography>

              <Stack spacing={1.2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PlaceIcon fontSize="small" />
                  <Typography>Room 5317, Block B, Guangzhou</Typography>
                </Stack>

                <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", my: 1 }} />

                <Stack direction="row" spacing={1} alignItems="center">
                  <AccessTimeIcon fontSize="small" />
                  <Typography>
                    Lun - Ven: 8h00 - 18h00 • Sam: 9h00 - 15h00 • Dim: Fermé
                  </Typography>
                </Stack>

                <Button
                  variant="contained"
                  href="#top"
                  sx={{ mt: 1, borderRadius: 2, fontWeight: 900, alignSelf: "flex-start" }}
                >
                  Retour en haut
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Footer - CENTERED */}
      <Box sx={{ bgcolor: "#0B1220", color: "white", py: 3 }}>
        <Container sx={{ textAlign: "center" }}>
          <Typography variant="body2" sx={{ opacity: 0.92 }}>
            © 2025 DIEU EST NOTRE FORCE — Agence de Transfert
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.75 }}>
            Sécurité • Rapidité • Confiance
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default Home;
